import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CollectionPurpose,
  PurposeCollectionDto,
  PurposeCollectionsResponse,
  PurposeCollectionsSetupResult,
} from '@bower/shared';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { apiService } from '../../services/api.service';
import {
  closePurposeCollectionsOffer,
  getPurposeCollections,
} from '../../services/api/projects.api';
import { queryKeys } from '../../lib/queryClient';
import { ApiResponseError } from '../../services/api/http-client.types';

export const purposeCollectionsKey = queryKeys.purposeCollections;

/**
 * The workspace's suggested collections (Protocols, Meeting notes, Live
 * sessions, Lab equipment, Literature) the caller can see, found by purpose,
 * never by name. A missing key means none: the caller falls back.
 */
export function usePurposeCollections(enabled = true) {
  const { workspace } = useWorkspace();
  const workspaceId = workspace?.id;
  return useQuery({
    queryKey: purposeCollectionsKey(workspaceId),
    queryFn: async (): Promise<PurposeCollectionsResponse> => {
      const response = await getPurposeCollections(apiService);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to load suggested collections');
      }
      return response.data as PurposeCollectionsResponse;
    },
    enabled: enabled && !!workspaceId,
    staleTime: 30_000,
  });
}

/** The purpose of the collection with this id, if it is one of the suggested ones. */
export function purposeOfCollection(
  data: PurposeCollectionsResponse | undefined,
  collectionId: string | null | undefined,
): CollectionPurpose | null {
  // `?? {}`: an unexpected answer must never break an empty state.
  if (!collectionId) return null;
  const match = Object.values(data?.collections ?? {}).find(
    (c): c is PurposeCollectionDto => !!c && c.id === collectionId,
  );
  return match?.purpose ?? null;
}

/**
 * Create types that file into a suggested collection when no collection is in
 * view. Voice notes are not here: dictation and meetings share the tile, and
 * the mode is only chosen inside the note.
 */
const PURPOSE_FOR_CREATE_TYPE: Readonly<Record<string, CollectionPurpose>> = {
  protocol: 'protocols',
  equipment: 'equipment',
};

/** The suggested collection a create of `type` defaults to, when the caller can add to it. */
export function purposeCollectionForCreateType(
  data: PurposeCollectionsResponse | undefined,
  type: string | null,
): PurposeCollectionDto | null {
  const purpose = type ? PURPOSE_FOR_CREATE_TYPE[type] : undefined;
  const collection = purpose ? data?.collections?.[purpose] : undefined;
  return collection?.canWrite ? collection : null;
}

/** Accept or dismiss the offer; refreshes the collection lists on success. */
export function useClosePurposeCollectionsOffer() {
  const { workspace } = useWorkspace();
  const queryClient = useQueryClient();
  return useMutation({
    // A retried setup would only find the offer closed by the first attempt.
    retry: false,
    mutationFn: async (outcome: 'setup' | 'dismiss'): Promise<PurposeCollectionsSetupResult> => {
      const response = await closePurposeCollectionsOffer(apiService, outcome);
      if (!response.success) {
        throw new ApiResponseError(
          response.error?.message || 'Could not set up suggested collections',
          response.error?.code,
        );
      }
      return response.data as PurposeCollectionsSetupResult;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: purposeCollectionsKey(workspace?.id) });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projects.list(workspace?.id ?? null),
      });
      void queryClient.invalidateQueries({ queryKey: ['workspace-items'] });
      void queryClient.invalidateQueries({ queryKey: ['hierarchy'] });
    },
  });
}
